import React, { useEffect, useMemo, useState } from 'react';

import { Icon, Spinner } from 'components';
import { ButtonV3 } from 'components/ComponentV2';
import Tooltip from 'components/Tooltip';
import { SortableItem, SortableList, Trigger } from 'components/Sortable';
import {
  CreateNewPlanPayload,
  SponsorShipPlanType,
} from 'lib/models/sponsorship-plans';
import { createArrayDeepCopy } from 'utils/common.utils';
import { useSponsorshipPlans } from '../useSponsorshipPlans';
import { PlanCard } from './PlanCard/PlanCard.component';
import { CreatePlanModal } from './CreatePlanModal/CreatePlanModal.component';
import styles from './Plans.module.scss';

interface PlansProps {
  plansPageUrl: string;
  goBack: () => void;
  copyOffersLink: () => void;
}

export const Plans: React.FC<PlansProps> = (props: PlansProps) => {
  const { plansPageUrl, goBack, copyOffersLink } = props;
  const { isLoading, sponsorshipPlans, deletePlan, updatePlan } =
    useSponsorshipPlans();

  const [sponsorshipData, setSponsorshipData] = useState<SponsorShipPlanType[]>(
    []
  );
  const [showCreatePlan, setShowCreatePlan] = useState<boolean>(false);
  const [editPlanData, setEditPlanData] = useState<
    SponsorShipPlanType | undefined
  >(undefined);

  useEffect(() => {
    if (sponsorshipPlans) {
      setSponsorshipData(createArrayDeepCopy(sponsorshipPlans));
    }
  }, [sponsorshipPlans]);

  const pageDisplayUrl = useMemo(() => {
    const index = plansPageUrl.indexOf('huzzle');
    return plansPageUrl.slice(index, plansPageUrl.length);
  }, [plansPageUrl]);

  const toggleCreatePlanModal = (editPlan?: SponsorShipPlanType) => () => {
    setShowCreatePlan((show) => !show);
    setEditPlanData(editPlan);
  };

  const onDragEnd = async ({
    oldIndex: oldPosition,
    newIndex: newPosition,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    if (sponsorshipData && oldPosition !== newPosition) {
      const planData = sponsorshipData.find(
        (plan) => plan.position === oldPosition
      );
      const oldSponsorData = createArrayDeepCopy(sponsorshipData);
      const temp = createArrayDeepCopy(sponsorshipData);
      const oldIndex = sponsorshipData.findIndex(
        (data) => data.position === oldPosition
      );
      const newIndex = sponsorshipData.findIndex(
        (data) => data.position === newPosition
      );
      temp.splice(oldIndex, 1, sponsorshipData[newIndex]);
      temp.splice(newIndex, 1, sponsorshipData[oldIndex]);
      setSponsorshipData(temp);
      if (planData) {
        planData.position = newPosition;
        const response = await updatePlan(
          planData.id,
          planData as unknown as CreateNewPlanPayload
        );
        if (!response) {
          setSponsorshipData(oldSponsorData);
        }
      }
    }
  };

  return (
    <>
      {isLoading && <Spinner wrapper showBackDrop size="small" />}
      <div className={styles.Plans}>
        <div className={styles.PlansHeader}>
          <div className={styles.breadcrumb} onClick={goBack}>
            <Icon size="large" iconName="chevron-left" />
            <p>Sponsorships</p>
          </div>
          <div className={styles.PlansHeaderInfo}>
            <div>
              <p className={styles.title}>Plans</p>
              <p className={styles.caption}>3 plans</p>
            </div>
            <div className={styles.headerAction}>
              <Tooltip
                className={styles.copyLinkTooltip}
                childrenClassName={styles.linkContainer}
                content="Use your invitation link to add your existing sponsors and invite them to huzzle. Click to copy."
                color="dark"
                xAlign="right"
              >
                <div
                  className={styles.copyLinkContainer}
                  onClick={copyOffersLink}
                >
                  <Icon iconName="icon_link" size="small" />
                  <p>{pageDisplayUrl}</p>
                  <div className={styles.copyIcon}>
                    <Icon iconName="icon_copy" size="small" />
                  </div>
                </div>
              </Tooltip>
              {sponsorshipPlans && sponsorshipPlans.length !== 3 && (
                <ButtonV3
                  color={sponsorshipPlans ? 'primary' : 'secondary'}
                  onClick={toggleCreatePlanModal()}
                >
                  Create a plan
                </ButtonV3>
              )}
            </div>
          </div>
        </div>
        <div className={styles.PlansContent}>
          {sponsorshipPlans && sponsorshipData && sponsorshipData.length > 0 && (
            <SortableList
              axis="x"
              lockAxis="x"
              lockToContainerEdges={true}
              useDragHandle={true}
              onSortEnd={onDragEnd}
            >
              {sponsorshipData.map((plan) => (
                <SortableItem
                  key={plan.id}
                  index={plan.position}
                  className={styles.SponsorshipPlansRow}
                >
                  <Tooltip
                    content="Drag to re-order"
                    color="dark"
                    isV2Design
                    position="top"
                    style={{ width: '125px' }}
                  >
                    <Trigger className={styles.drag}>
                      <Icon
                        iconName="drag-and-drop"
                        className={styles.dragIcon}
                      />
                    </Trigger>
                  </Tooltip>
                  <PlanCard
                    plan={plan}
                    onDeletePlan={() => deletePlan(plan.id)}
                    onEditPlan={toggleCreatePlanModal(plan)}
                  />
                </SortableItem>
              ))}
            </SortableList>
          )}
          {sponsorshipPlans &&
            sponsorshipPlans.length > 0 &&
            sponsorshipPlans.length !== 3 && (
              <PlanCard isEmpty onCreatePlan={toggleCreatePlanModal()} />
            )}
          {sponsorshipPlans && sponsorshipPlans.length === 0 && (
            <div className={styles.PlansEmpty}>
              <Icon
                iconName="icon_info"
                size="large"
                className={styles.infoIcon}
              />
              <div className={styles.info}>
                <p>
                  Create up to 3 pricing plans for attracting companies to
                  sponsor your society.
                </p>
                <ButtonV3 size="large" onClick={toggleCreatePlanModal()}>
                  Create a plan
                </ButtonV3>
              </div>
            </div>
          )}
        </div>
      </div>
      <CreatePlanModal
        open={showCreatePlan}
        onClose={toggleCreatePlanModal()}
        editPlanData={editPlanData}
      />
    </>
  );
};
